/**
 * Helpers de mascara/normalizacao do RUC paraguaio.
 *
 * Formato aceito: "NNNNNNN-D" — apenas digitos, um unico hifen e um unico
 * digito verificador (DV) apos o hifen (ex.: 8255140-5).
 *
 * Extraido do /signup para ser reaproveitado em qualquer tela que peca RUC
 * da empresa (ex.: modal "Novo contrato" da area do contador).
 */

/**
 * Filtra/normaliza o valor digitado no campo RUC.
 * - Remove tudo que nao for digito ou hifen.
 * - Remove hifens iniciais (o DV nao pode vir antes dos digitos).
 * - Mantem apenas o primeiro hifen e limita o DV a 1 digito.
 *
 * @param valor Valor bruto digitado pelo usuario.
 * @returns Valor ja filtrado, pronto para exibir no input e enviar a API.
 */
export function filtrarValorRuc(valor: string | null | undefined): string {
  let filtrado = (valor || '').replace(/[^0-9-]/g, '');

  // Remove hifens iniciais (DV nao pode vir antes dos digitos)
  filtrado = filtrado.replace(/^-+/, '');

  // Mantem apenas o primeiro hifen e limita o DV a 1 digito
  const partes = filtrado.split('-');
  if (partes.length > 1) {
    filtrado = partes[0] + '-' + partes[1].slice(0, 1);
  }

  return filtrado;
}

/**
 * Aplica `filtrarValorRuc` diretamente sobre o evento `(input)` de um campo
 * de texto, corrigindo o valor exibido e devolvendo o valor limpo para o
 * componente atualizar o seu modelo.
 *
 * @param event Evento nativo `input` do campo RUC.
 * @returns Valor ja filtrado.
 */
export function filtrarRucDoEvento(event: Event): string {
  const input = event.target as HTMLInputElement;
  const filtrado = filtrarValorRuc(input.value);
  input.value = filtrado;
  return filtrado;
}

/**
 * Calcula o digito verificador (DV) de um RUC paraguaio — modulo 11 com pesos
 * de 2 a 11 da direita para a esquerda (algoritmo da DNIT).
 *
 * Espelha `StringUtils.computeDV` do backend: a regra precisa ser IDENTICA,
 * senao a tela aceita o que o backend recusa (ou o contrario).
 *
 * @param base Parte do RUC antes do hifen (somente digitos).
 * @returns O DV esperado, ou `null` se `base` nao for composta so de digitos.
 */
export function calcularDvRuc(base: string | null | undefined): number | null {
  const digitos = (base || '').trim();
  if (!/^\d+$/.test(digitos)) {
    return null;
  }

  let peso = 2;
  let total = 0;
  for (let i = digitos.length - 1; i >= 0; i--) {
    if (peso > 11) {
      peso = 2;
    }
    total += Number(digitos[i]) * peso;
    peso++;
  }

  const resto = total % 11;
  return resto > 1 ? 11 - resto : 0;
}

/**
 * Diz se o DV digitado confere com o calculado.
 *
 * So julga RUC COMPLETO ("BASE-D"). Sem hifen/DV devolve `null` — o usuario
 * ainda pode estar digitando, e RUC sem DV nao e invalido por si so.
 *
 * IMPORTANTE: RUC valido NAO significa RUC existente na base publica. A base
 * externa pode estar desatualizada; "nao encontrado" nunca e "invalido".
 *
 * @param ruc Valor do campo (ex.: "80133485-3").
 * @returns `true` confere, `false` DV errado, `null` sem DV para conferir.
 */
export function isDvRucValido(ruc: string | null | undefined): boolean | null {
  const valor = (ruc || '').trim();
  const partes = valor.split('-');
  if (partes.length !== 2 || !/^\d$/.test(partes[1])) {
    return null;
  }
  const esperado = calcularDvRuc(partes[0]);
  if (esperado === null) {
    return null;
  }
  return esperado === Number(partes[1]);
}
